import { useMemo } from "react";

import { useGetCustomerOrdersQuery } from "@jti/store/slices/magentoApiSlice";
import { useUser } from "@jti/ui/hooks/useUser";

import { PURCHASER_ORDER_STATUSES } from "../utils";

type Options = {
  enabled?: boolean;
};

type HookReturnType = {
  lastSuccessfulPurchaseDate: Date | null;
  isLoading: boolean;
};

const safeParse = (date: Date | string | number | null | undefined): Date | null => {
  if (date === null || date === undefined) return null;
  const parsed = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const useLastSuccessfulPurchaseDate = ({ enabled = true }: Options = {}): HookReturnType => {
  const { isUserLoggedIn, isCheckingUser } = useUser();

  const { data, isLoading } = useGetCustomerOrdersQuery(undefined, {
    skip: !enabled || isCheckingUser || !isUserLoggedIn,
  });

  return useMemo(() => {
    if (!enabled || !isUserLoggedIn || isLoading) {
      return { lastSuccessfulPurchaseDate: null, isLoading };
    }

    const ordersItems = data?.customer?.orders?.items ?? [];
    if (ordersItems.length === 0) {
      return { lastSuccessfulPurchaseDate: null, isLoading };
    }

    const sortedOrdersByDate = ordersItems.toSorted(
      (a, b) => (safeParse(b?.order_date)?.getTime() ?? 0) - (safeParse(a?.order_date)?.getTime() ?? 0)
    );

    const recentOrder = sortedOrdersByDate[0];

    if (!recentOrder?.status || !PURCHASER_ORDER_STATUSES.includes(recentOrder.status)) {
      return { lastSuccessfulPurchaseDate: null, isLoading };
    }

    if (!recentOrder?.order_date) return { lastSuccessfulPurchaseDate: null, isLoading };

    return { lastSuccessfulPurchaseDate: safeParse(recentOrder.order_date), isLoading };
  }, [data?.customer?.orders?.items, enabled, isLoading, isUserLoggedIn]);
};
